import { useState, useEffect } from "react";
import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { DatePicker } from "@/components/ui/date-picker";
import { UploadMultiFiles } from "@/components/ui/upload-multi-files";
import AutocompleteSelect from "@/components/ui/autocomplete-select";
import { Link, Euro, Plus, Trash, Ticket } from "lucide-react";
import { FlightActivity } from "../models/activity.model";
import { getFlightCompanies } from "@/components/utils/flight-companies";
import { getAirports } from "@/components/utils/airports";

interface FlightModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (activity: Partial<FlightActivity>) => void;
  icon?: React.ReactNode;
  initialData?: Partial<FlightActivity>;
  itineraryId: string;
  itineraryStartDate?: string | null;
  itineraryEndDate?: string | null;
}

const FlightModal = ({ isOpen, onClose, onSave, icon, initialData, itineraryId, itineraryStartDate, itineraryEndDate }: FlightModalProps) => {
  // Generate temporary UUID for new activities
  const [activityId] = useState<string>(() =>
    initialData?.id || crypto.randomUUID()
  );

  const [formData, setFormData] = useState<Partial<FlightActivity>>({
    id: activityId,
    include_price: true,
    title: '',
    link: '',
    address: '',
    travels: [
      {
        flightNumber: '',
        airline: '',
        link: '',
        from: '',
        to: '',
        fromDate: undefined,
        fromTime: '',
        toDate: undefined,
        toTime: ''
      }
    ],
    cost: 0,
    description: '',
    documentation: []
  });

  // Update form data when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({ ...initialData, id: activityId });
    }
  }, [initialData, activityId]);

  const [errors, setErrors] = useState<Record<string, boolean>>({});

  // Create options for AutocompleteSelect components
  const airlineOptions = getFlightCompanies().map(company => ({
    value: company,
    label: company
  }));

  const airportOptions = getAirports().map(airport => ({
    value: airport,
    label: airport
  }));

  const validateForm = () => {
    const requiredFields = ['cost', 'description'];
    const newErrors: Record<string, boolean> = {};

    // Validate main form fields
    requiredFields.forEach(field => {
      const value = formData[field as keyof FlightActivity];
      if (field === 'cost') {
        newErrors[field] = value === null || value === undefined || (value as number) < 0;
      } else {
        newErrors[field] = !value || (typeof value === 'string' && value.trim() === '');
      }
    });

    // Validate flights array
    if (!formData.travels || formData.travels.length === 0) {
      newErrors.travels = true;
    } else {
      // Validate each flight's required fields
      formData.travels.forEach((travel, index) => {
        const travelRequiredFields = ['flightNumber', 'airline', 'link', 'from', 'to', 'fromDate', 'fromTime', 'toDate', 'toTime'];
        travelRequiredFields.forEach(field => {
          const value = travel[field as keyof typeof travel];
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            newErrors[`travel_${index}_${field}`] = true;
          }
        });

        // // Validate flight number format (should contain letters and numbers)
        // if (travel.flightNumber && !/^[A-Z]{2,3}\d{1,4}$/i.test(travel.flightNumber.trim())) {
        //   newErrors[`travel_${index}_flightNumber`] = true;
        // }

        // Validate that from and to airports are different
        if (travel.from && travel.to && travel.from.trim() === travel.to.trim()) {
          newErrors[`travel_${index}_from`] = true;
          newErrors[`travel_${index}_to`] = true;
        }
      })
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  const getDateWithOffset = (dateString: string | null | undefined, offset: number) => {
    if (!dateString) return undefined;
    const baseDate = new Date(dateString);
    if (Number.isNaN(baseDate.getTime())) return undefined;
    baseDate.setDate(baseDate.getDate() + offset);
    return baseDate;
  };

  const fromMinDate = getDateWithOffset(itineraryStartDate, -1);
  const fromMaxDate = getDateWithOffset(itineraryEndDate, 1);
  const toMinDate = getDateWithOffset(itineraryStartDate, -1);
  const toMaxDate = getDateWithOffset(itineraryEndDate, 1);

  const addFlight = () => {
    setFormData(prev => ({
      ...prev,
      travels: [
        ...(prev.travels || []),
        {
          flightNumber: '',
          airline: '',
          link: '',
          from: '',
          to: '',
          fromDate: '',
          fromTime: '',
          toDate: '',
          toTime: ''
        }
      ]
    }));
  };

  const removeFlight = (index: number) => {
    setFormData(prev => ({
      ...prev,
      travels: prev.travels?.filter((_, i) => i !== index) || []
    }));
  };

  const updateFlight = (index: number, field: keyof FlightActivity['travels'][0], value: string) => {
    setFormData(prev => ({
      ...prev,
      travels: prev.travels?.map((flight, i) => {
        if (i !== index) {
          return flight;
        }

        const updatedFlight = { ...flight, [field]: value };

        // se sto settando fromDate controllo che toDate sia dopo, altrimenti la resetto
        if (field === 'fromDate' && updatedFlight.toDate) {
          const fromDateValue = new Date(value);
          const toDateValue = new Date(updatedFlight.toDate);
          if (!Number.isNaN(fromDateValue.getTime()) && !Number.isNaN(toDateValue.getTime()) && toDateValue < fromDateValue) {
            updatedFlight.toDate = '';
          }
        }

        return updatedFlight;
      }) || []
    }));
  };


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const title = formData.travels[0].from + ' → ' + formData.travels[0].to;

      onSave({
        ...formData,
        title,
        type: 'flight' as const,
        date: formData.travels[0].fromDate
      });
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          {icon} Flight
        </div>
      }
      size="lg"
      footer={
        <div className="flex w-full justify-between">
          <Button type="button" size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm" form="flight-form">
            Confirm
          </Button>
        </div>
      }
    >
      <form id="flight-form" onSubmit={handleSubmit} className="space-y-4 p-6">
        {/* Include Price Switch */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
          <div>
            <label className="text-sm font-medium text-gray-700">Include Price</label>
            <p className="text-xs text-gray-500">Included in the total price of the package</p>
          </div>
          <Switch
            checked={formData.include_price || false}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, include_price: checked }))}
          />
        </div>

        {/* Flights Section */}
        <div className="flex flex-col items-center justify-center gap-4">
          {formData.travels?.map((travel, index) => (
            <Card key={index} className="border border-gray-200 w-full">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium">Flight {index + 1}</CardTitle>
                  {formData.travels && formData.travels.length > 1 && index !== 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFlight(index)}
                      className=""
                    >
                      <Trash className="h-4 w-4" /> Delete flight
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Flight Number, Airline, Link */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="relative">
                    <Ticket className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                    <Input
                      value={travel.flightNumber}
                      onChange={(e) => updateFlight(index, 'flightNumber', e.target.value)}
                      placeholder="Flight Number"
                      className="pl-10"
                      hasError={errors[`travel_${index}_flightNumber`]}
                    />
                  </div>
                  <AutocompleteSelect
                    options={airlineOptions}
                    value={travel.airline}
                    onValueChange={(value) => updateFlight(index, 'airline', value)}
                    placeholder="Airline"
                    searchPlaceholder="Search airlines..."
                    emptyMessage="No airlines found"
                    manual={true}
                    filterMode="includes"
                    maxWidth="320px"
                    hasError={errors[`travel_${index}_airline`]}
                  />
                  <div className="relative">
                    <Link className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                    <Input
                      value={travel.link}
                      onChange={(e) => updateFlight(index, 'link', e.target.value)}
                      placeholder="Link"
                      className="pl-10"
                      hasError={errors[`travel_${index}_link`]}
                    />
                  </div>
                </div>

                {/* From, FromDate, FromTime */}
                <div className="space-y-4">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="w-14 text-sm font-medium">From</div>
                    <div className="flex-1">
                      <AutocompleteSelect
                        options={airportOptions}
                        value={travel.from}
                        onValueChange={(value) => updateFlight(index, 'from', value)}
                        placeholder="Departure Airport"
                        searchPlaceholder="Search airports..."
                        emptyMessage="No airports found"
                        manual={true}
                        filterMode="includes"
                        maxWidth="360px"
                        hasError={errors[`travel_${index}_from`]}
                      />
                    </div>
                    <div className="flex flex-row gap-4">
                      <div className="w-52 flex-0">
                        <DatePicker
                          value={travel.fromDate}
                          onSelect={(date) => updateFlight(index, 'fromDate', date)}
                          placeholder="Departure Date"
                          hasError={errors[`travel_${index}_fromDate`]}
                          minDate={fromMinDate}
                          maxDate={fromMaxDate}
                        />
                      </div>
                      <div className="w-24">
                        <Input
                          type="time"
                          value={travel.fromTime}
                          onChange={(e) => updateFlight(index, 'fromTime', e.target.value)}
                          placeholder="HH:MM"
                          hasError={errors[`travel_${index}_fromTime`]}
                        />
                      </div>
                    </div>
                  </div>

                  {/* To, ToDate, ToTime */}
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="w-14 text-sm font-medium">To</div>
                    <div className="flex-1 flex-shrink">
                      <AutocompleteSelect
                        options={airportOptions}
                        value={travel.to}
                        onValueChange={(value) => updateFlight(index, 'to', value)}
                        placeholder="Arrival Airport"
                        searchPlaceholder="Search airports..."
                        emptyMessage="No airports found"
                        manual={true}
                        filterMode="includes"
                        maxWidth="360px"
                        hasError={errors[`travel_${index}_to`]}
                      />
                    </div>
                    <div className="flex flex-row gap-4">
                      <div className="w-52">
                        <DatePicker
                          value={travel.toDate}
                          onSelect={(date) => updateFlight(index, 'toDate', date)}
                          placeholder="Arrival Date"
                          hasError={errors[`travel_${index}_toDate`]}
                          minDate={travel.fromDate ? travel.fromDate : toMinDate}
                          maxDate={toMaxDate}
                        />
                      </div>
                      <div className="w-24">
                        <Input
                          type="time"
                          value={travel.toTime}
                          onChange={(e) => updateFlight(index, 'toTime', e.target.value)}
                          placeholder="HH:MM"
                          hasError={errors[`travel_${index}_toTime`]}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Add Flight Button */}
          <Button
            type="button"
            variant="secondary"
            onClick={addFlight}
            className="w-fit"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add Flight
          </Button>
        </div>

        {/* Price Card */}
        <Card>
          <CardHeader>
            <CardTitle>Price</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-center gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-900" />
                  <Input
                    id="cost"
                    type="number"
                    min="0"
                    step="1"
                    value={formData.cost}
                    onChange={(e) => setFormData(prev => ({ ...prev, cost: parseFloat(e.target.value) }))}
                    placeholder="Amount (only visible to you)"
                    className="pl-10"
                    hasError={errors.cost}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Description Card */}
        <Card>
          <CardHeader>
            <CardTitle>Note</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              id="description"
              value={formData.description || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Share client preferences with Place2Be, such as fare type, luggage preferences, or extras."
              rows={6}
              hasError={errors.description}
            />
          </CardContent>
        </Card>

        {/* Documentation Card */}
        <Card>
          <CardHeader>
            <CardTitle>Documentation</CardTitle>
          </CardHeader>
          <CardContent>
            <UploadMultiFiles
              files={formData.documentation || []}
              onFilesChange={(files) => setFormData(prev => ({ ...prev, documentation: files }))}
              label="Upload boarding pass when available"
              maxFiles={5}
              itineraryId={itineraryId}
              activityId={activityId}
            />
          </CardContent>
        </Card>
      </form>
    </Modal>
  );
};

export default FlightModal;
